import type {AudienceListAction} from 'src/action-creators/audience-list';
import type {
  AgentDetails,
  AudienceRule,
  AudienceListStatus,
  AudienceListSource,
  AssociatedResources,
} from 'src/types/audience-list';

import {
  createInclusionBlockIdentifier,
  getDefaultExclusionBlock,
  getDefaultInclusionBlock,
  cloneRuleWithoutValues,
} from 'src/utils/audience-list';
import {
  UPDATE_STATE,
  UPDATE_ENTITY_TYPE,
  RECEIVE_LIST,
  RESET,
  SET_RULES,
  SET_ALL,
  RECEIVE_PUBLISHED_LIST_IDS,
  RECEIVE_UPDATED_LIST,
  RESET_JOURNEY_FLOW_FIELDS,
  RESET_AUTOMATION_WORKFLOW_FIELDS,
  ADD_INCLUSION_BLOCK,
  REMOVE_INCLUSION_BLOCK,
  CLONE_INCLUSION_BLOCK,
  SET_AUDIENCE_BLOCKS,
} from 'src/action-creators/audience-list';


export type AudienceListState = {
  id: string | null | undefined;
  name: string;
  entityType: string | null | undefined;
  inclusions: Map<string, AudienceRule[]>;
  exclusions: Map<string, AudienceRule[]>;
  associatedResources: {
    resource_type: string;
  }[];
  status: AudienceListStatus | null | undefined;
  source?: AudienceListSource | null | undefined;
  target?: string | null | undefined;
  lifeCycleGoal?: string | null | undefined;
  businessGoal?: string | null | undefined;
  all: boolean | null | undefined;
  ruleCreationType: string;
  metadata: {
    agentDetails: AgentDetails;
    associatedResources: AssociatedResources;
    lastActionAgentDetails: AgentDetails;
    associatedResourcesCount: number;
  };
};

const initialState = {
  id: null,
  name: '',
  entityType: null,
  inclusions: new Map(getDefaultInclusionBlock()),
  exclusions: new Map(getDefaultExclusionBlock()),
  associatedResources: [],
  status: null,
  target: null,
  lifeCycleGoal: null,
  businessGoal: null,
  all: false,
  ruleCreationType: 'new',
  source: null,
  metadata: {
    agentDetails: {},
    associatedResources: [],
    lastActionAgentDetails: {},
    associatedResourcesCount: 0,
  },
};

export default (
  state: AudienceListState = initialState,
  action: AudienceListAction,
): AudienceListState => {
  switch (action.type) {
    case RECEIVE_LIST: {
      const {
        id,
        status,
        inclusions,
        exclusions,
        name,
        entityType,
        all,
        source,
        ...restList
      } = action.payload;
      return {
        ...state,
        id,
        status,
        // @ts-expect-error - TS2322 - Type 'Map<string, AudienceRule[]> | undefined' is not assignable to type 'Map<string, AudienceRule[]>'.
        inclusions,
        // @ts-expect-error - TS2322 - Type 'Map<string, AudienceRule[]> | undefined' is not assignable to type 'Map<string, AudienceRule[]>'.
        exclusions,
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        name,
        entityType,
        all,
        source,
        ...restList,
      };
    }
    case UPDATE_STATE: {
      const updatePayload = action.payload;
      return {
        ...state,
        ...updatePayload,
      };
    }
    case UPDATE_ENTITY_TYPE: {
      return {
        ...state,
        entityType: action.payload,
        inclusions: new Map(getDefaultInclusionBlock()),
        exclusions: new Map(getDefaultExclusionBlock()),
      };
    }
    case RESET: {
      // return cloneDeep(initialState)
      return {...initialState};
    }
    case SET_RULES: {
      const {blockId, rules} = action.payload;
      const isInclusion = !!state.inclusions.get(blockId);
      if (isInclusion) {
        return {
          ...state,
          all: false,
          inclusions: new Map([...state.inclusions]).set(blockId, rules),
        };
      }
      const isExclusion = !!state.exclusions.get(blockId);
      if (isExclusion) {
        return {
          ...state,
          all: false,
          exclusions: new Map([...state.exclusions]).set(blockId, rules),
        };
      }
      return {...state};
    }
    case SET_ALL: {
      return {
        ...state,
        all: action.payload,
      };
    }
    case RECEIVE_PUBLISHED_LIST_IDS: {
      const publishedLists = action.payload;
      // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
      const status = publishedLists.includes(state.id)
        ? 'published'
        : state.status;

      return {
        ...state,
        status,
      };
    }
    case RECEIVE_UPDATED_LIST: {
      const {id, status, inclusions, exclusions, name, all} = action.payload;
      return {
        ...state,
        id,
        status,
        // @ts-expect-error - TS2322 - Type 'Map<string, AudienceRule[]> | undefined' is not assignable to type 'Map<string, AudienceRule[]>'.
        inclusions,
        // @ts-expect-error - TS2322 - Type 'Map<string, AudienceRule[]> | undefined' is not assignable to type 'Map<string, AudienceRule[]>'.
        exclusions,
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        name,
        all: all ?? state.all,
      };
    }
    case RESET_JOURNEY_FLOW_FIELDS: {
      const {businessGoal, entityType, target, lifeCycleGoal} = state;
      return {
        ...initialState,
        businessGoal,
        entityType,
        target,
        lifeCycleGoal,
      };
    }
    case RESET_AUTOMATION_WORKFLOW_FIELDS: {
      const {businessGoal, entityType, target, lifeCycleGoal} = state;
      return {
        ...initialState,
        businessGoal,
        entityType,
        target,
        lifeCycleGoal,
      };
    }
    case ADD_INCLUSION_BLOCK: {
      return {
        ...state,
        inclusions: state.inclusions.set(createInclusionBlockIdentifier(), []),
      };
    }
    case REMOVE_INCLUSION_BLOCK: {
      return {
        ...state,
        inclusions: new Map(
          [...state.inclusions.entries()].filter(
            ([key, _]: [any, any]) => key !== action.payload,
          ),
        ),
      };
    }
    case CLONE_INCLUSION_BLOCK: {
      return {
        ...state,
        //insertion order is important here, hence the drill
        inclusions: [...state.inclusions.entries()].reduce(
          (accumulator, [key, value]: [any, any]) => {
            accumulator.set(key, value);
            if (key === action.payload) {
              accumulator.set(
                createInclusionBlockIdentifier(),
                cloneRuleWithoutValues(value),
              );
            }
            return accumulator;
          },
          new Map(),
        ),
      };
    }
    case SET_AUDIENCE_BLOCKS: {
      const {inclusions, exclusions, all} = action.payload;
      return {
        ...state,
        inclusions,
        exclusions,
        all,
      };
    }
  }

  return state;
};
